import { TFunction } from 'i18next';
import { Calendar, CalendarClock, CalendarRange, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PeriodType } from '@/features/forecasts/api/get-kpi-forecasts';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown';
import { Label } from '@/shared/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';

const periodToIcon = (period: PeriodType) => {
  switch (period) {
    case 'monthly':
      return <Calendar className="size-4" />;
    case 'quarterly':
      return <CalendarRange className="size-4" />;
    case 'yearly':
      return <CalendarClock className="size-4" />;
  }
};

const periodToString = (
  period: PeriodType,
  t: TFunction<'forecast', 'PeriodTypes'>,
) => {
  switch (period) {
    case 'monthly':
      return t('monthly');
    case 'quarterly':
      return t('quarterly');
    case 'yearly':
      return t('yearly');
  }
};

export const ForecastPeriodSelector = ({
  currentPeriod,
  onUpdatePeriod,
}: {
  currentPeriod: PeriodType;
  onUpdatePeriod: (period: PeriodType) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('forecast', { keyPrefix: 'ForecastsHub' });
  const { t: tPeriodTypes } = useTranslation('forecast', {
    keyPrefix: 'PeriodTypes',
  });

  const availablePeriods: PeriodType[] = ['monthly', 'quarterly', 'yearly'];

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" btnType="secondary">
          {periodToIcon(currentPeriod)}
          {periodToString(currentPeriod, tPeriodTypes)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        sideOffset={12}
        alignOffset={-7}
      >
        <div className="mx-4 my-2 flex flex-row items-center">
          <DropdownMenuLabel className="flex-1 p-0 text-xs font-normal">
            {t('time_period')}
          </DropdownMenuLabel>
          <XIcon
            className="cursor-pointer text-white"
            size={'15px'}
            onClick={() => setIsOpen(false)}
          />
        </div>
        <DropdownMenuSeparator className="my-2 h-[0.75px]" />
        <RadioGroup value={currentPeriod} onValueChange={onUpdatePeriod}>
          <div className="mx-3 my-3 flex flex-row justify-evenly gap-6">
            {availablePeriods.map((period) => (
              <div key={period} className="flex flex-col items-center gap-4">
                <RadioGroupItem value={period} id={period} />
                <Label htmlFor={period}>
                  <div className="flex flex-row items-center gap-2">
                    {periodToIcon(period)}
                    {periodToString(period, tPeriodTypes)}
                  </div>
                </Label>
              </div>
            ))}
          </div>
        </RadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
